import { useCallback, useEffect, useState } from "react";
import { ProposalTable } from "../../components/BelowCost";
import { SALES_RIGHTS, type BelowCostApprovalDto, type SalesRight, type SalesRightsDto } from "@delta/shared";
import { useLang } from "../../i18n";
import { api } from "../../lib/api";
import { errorText } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { Field, Modal } from "../../components/Modal";
import { PageHead, Shell } from "../../components/Shell";
import { useToast } from "../../components/Toast";
import { amt } from "../ledger/common";

// Sales › Price controls (migration 039; docs/correction-pass-2026-10-29.md, section 3). The three selling price
// rights per user (the business owner grants them; everyone sees their own) and the below-cost approvals: pending
// requests for approvers, a user's own requests otherwise. Approving needs a reason; both decisions are audited.

const RIGHT_LABEL: Record<SalesRight, string> = { edit_price: "pcEditPrice", approve_below_cost: "pcApprove", view_cost: "pcViewCost" };

export function PriceControls() {
  const { t, lang } = useLang();
  const toast = useToast();
  const [rights, setRights] = useState<SalesRightsDto | null>(null);
  const [reqs, setReqs] = useState<BelowCostApprovalDto[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [deciding, setDeciding] = useState<{ a: BelowCostApprovalDto; approve: boolean } | null>(null);
  const when = (iso: string) => new Date(iso).toLocaleString(lang === "ar" ? "ar-SA-u-nu-latn-ca-gregory" : "en-GB", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Riyadh" });
  const L = (x: { nameEn: string; nameAr: string }) => (lang === "ar" ? x.nameAr : x.nameEn);

  const load = useCallback(async () => {
    setFailed(null);
    try {
      const [r, q] = await Promise.all([api<SalesRightsDto>("GET", "/sales-rights"), api<BelowCostApprovalDto[]>("GET", "/below-cost-approvals")]);
      setRights(r); setReqs(q);
    } catch (e) { setFailed(errorText(t, e)); }
  }, [t]);
  useEffect(() => { void load(); }, [load]);
  // Requests arrive while an approver keeps the page open.
  useEffect(() => {
    const id = window.setInterval(() => { api<BelowCostApprovalDto[]>("GET", "/below-cost-approvals").then(setReqs, () => undefined); }, 10000);
    return () => window.clearInterval(id);
  }, []);

  async function toggle(userId: string, right: SalesRight, granted: boolean) {
    setBusy(`${userId}:${right}`);
    try { setRights(await api<SalesRightsDto>("PUT", `/sales-rights/${userId}/${right}`, { granted })); } catch (e) { toast(errorText(t, e)); } finally { setBusy(null); }
  }

  const approver = !!rights?.mine.includes("approve_below_cost");
  const body = failed ? (
    <section className="card"><div className="state-box error"><Icon name="alert" /><h3>{t("loadErrorTitle")}</h3><p>{failed}</p><button className="btn btn-primary" onClick={() => void load()}><Icon name="refresh" />{t("retry")}</button></div></section>
  ) : !rights || !reqs ? <section className="card"><div className="state-box"><p>{t("loading")}</p></div></section> : (
    <div className="set-stack">
      <section className="set-sec">
        <div className="set-sec-h"><h2>{t("pcRequests")}</h2><p>{t("pcPolicy")}</p></div>
        <div className="card set-sec-b">
          {reqs.length === 0 ? <div className="state-box"><p>{t("pcNoRequests")}</p></div> : (
            <div className="pc-scroll"><table className="tbl pc-table" data-testid="pc-requests">
              <thead><tr><th>{t("date")}</th><th>{t("pcRequestedBy")}</th><th>{t("warehouse")}</th><th>{t("customer")}</th><th>{t("bcLine")}</th><th className="num">{t("bcValue")}</th><th className="num">{t("bcCost")}</th><th>{t("status")}</th><th /></tr></thead>
              <tbody>{reqs.map((a) => (
                <tr key={a.id} data-testid="pc-request" data-status={a.status}>
                  <td className="num">{when(a.requestedAt)}</td><td>{a.requestedByName}</td><td>{L(a.warehouse)}</td><td>{a.party?.name ?? t("walkInOption")}</td>
                  <td>{a.lines.map((l) => <div key={l.lineNo}>{l.item.code} {L(l.item)} · <span className="num">{l.qty}</span> {L(l.unit)} × <span className="num">{amt(l.price)}</span>{l.discountPct ? ` −${l.discountPct}%` : ""}</div>)}
                    {a.proposal && <details className="pc-proposal" data-testid="pc-proposal"><summary>{t("bcProposalOpen", { x: amt(a.proposal.totals.grand) })}</summary><ProposalTable p={a.proposal} /></details>}</td>
                  <td className="num">{a.lines.map((l) => <div key={l.lineNo}>{amt(l.value)}</div>)}</td>
                  <td className="num">{a.lines.map((l) => <div key={l.lineNo}>{l.costUnknown ? <span className="hint" data-testid="bc-cost-unknown">{t("bcCostUnknown")}</span> : l.cost === null ? <span className="hint">{t("bcCostHidden")}</span> : amt(l.cost)}</div>)}</td>
                  <td><span className={`pill ${a.status === "pending" ? "warn" : a.status === "rejected" ? "bad" : "good"}`}>{t(`pcStatus_${a.status}`)}</span>
                    {a.decidedByName && <div className="hint">{a.decidedByName}{a.reason ? `: ${a.reason}` : ""}</div>}</td>
                  <td className="end">{approver && a.status === "pending" && !a.mine && <span className="row-actions">
                    <button className="btn btn-sm btn-primary" data-testid="pc-approve" onClick={() => setDeciding({ a, approve: true })}>{t("pcApproveBtn")}</button>
                    <button className="btn btn-sm" data-testid="pc-reject" onClick={() => setDeciding({ a, approve: false })}>{t("pcRejectBtn")}</button></span>}</td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </div>
      </section>
      <section className="set-sec">
        <div className="set-sec-h"><h2>{t("pcTitle")}</h2><p>{t("pcSub")}</p>{!rights.canManage && <p className="hint">{t("pcOwnerOnly")}</p>}</div>
        <div className="card set-sec-b"><div className="pc-scroll">
          <table className="tbl pc-table" data-testid="pc-rights">
            <thead><tr><th>{t("user")}</th>{SALES_RIGHTS.map((r) => <th key={r} className="center">{t(RIGHT_LABEL[r])}</th>)}</tr></thead>
            <tbody>{rights.users.map((u) => (
              <tr key={u.id} data-testid="pc-user" data-user={u.phone}>
                <td><b>{u.name}</b> <span className="num hint">{u.phone}</span>{u.owner && <div className="hint">{t("pcOwner")}</div>}</td>
                {SALES_RIGHTS.map((r) => (
                  <td key={r} className="center">
                    <input type="checkbox" checked={u.rights.includes(r)} disabled={!rights.canManage || u.owner || busy === `${u.id}:${r}`}
                      onChange={(e) => void toggle(u.id, r, e.target.checked)} aria-label={`${t(RIGHT_LABEL[r])} · ${u.name}`} data-testid={`pc-${r}`} />
                  </td>
                ))}
              </tr>
            ))}</tbody>
          </table>
        </div></div>
      </section>
    </div>
  );

  return (
    <Shell navKey="/price-controls">
      <PageHead crumbs={[[t("home"), "/home"], [t("sales")], [t("pcTitle")]]} title={t("pcTitle")} />
      {body}
      {deciding && <Decide {...deciding} onClose={() => setDeciding(null)} onDone={(x) => { setDeciding(null); setReqs((q) => q?.map((y) => (y.id === x.id ? x : y)) ?? null); toast(t(`pcStatus_${x.status}`)); }} />}
    </Shell>
  );
}

function Decide({ a, approve, onClose, onDone }: { a: BelowCostApprovalDto; approve: boolean; onClose(): void; onDone(x: BelowCostApprovalDto): void }) {
  const { t } = useLang();
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | undefined>();
  const [alert, setAlert] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit() {
    if (approve && !reason.trim()) { setErr(t("requiredField")); return; }
    setBusy(true);
    try { onDone(await api<BelowCostApprovalDto>("POST", `/below-cost-approvals/${a.id}/${approve ? "approve" : "reject"}`, { reason: reason.trim() })); }
    catch (e) { setAlert(errorText(t, e)); } finally { setBusy(false); }
  }
  return (
    <Modal title={approve ? t("pcApproveBtn") : t("pcRejectBtn")} sub={a.requestedByName} onClose={onClose} onSubmit={() => void submit()} alert={alert} busy={busy} saveLabel={approve ? t("pcApproveBtn") : t("pcRejectBtn")}>
      <Field label={approve ? t("bcReason") : t("pcRejectReason")} req={approve} htmlFor="pc-reason" error={err} hint={t("bcReasonHint")} full>
        <textarea id="pc-reason" className="control" rows={2} maxLength={500} value={reason} onChange={(e) => { setReason(e.target.value); setErr(undefined); }} autoFocus />
      </Field>
    </Modal>
  );
}
